/**
 * Polymarket US — read-only notice (R1, org-pooled keys).
 *
 * Keys for the US venue belong to the ORG (the entity account) and are added
 * by managers under Org Settings, exactly like intl Polymarket. Traders only
 * need to know whether the org has one on this server. Renders nothing when
 * the relay reports the lane off (POLYMARKET_US_NOT_CONFIGURED).
 */
import { useEffect, useState } from 'react';
import { fetchPolymarketUsStatus, PolymarketUsRelayError, type PolymarketUsStatus } from '../../lib/polymarketUs/relay';

type State =
  | { kind: 'loading' }
  | { kind: 'off' }
  | { kind: 'ready'; status: PolymarketUsStatus }
  | { kind: 'error'; message: string };

function tail(keyId: string): string {
  return keyId.length > 8 ? `…${keyId.slice(-6)}` : keyId;
}

export function PolymarketUsOrgKeyNotice() {
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const status = await fetchPolymarketUsStatus();
        if (!cancelled) setState({ kind: 'ready', status });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof PolymarketUsRelayError && err.laneOff) setState({ kind: 'off' });
        else setState({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.kind === 'off' || state.kind === 'loading') return null;

  const orgKey = state.kind === 'ready' ? state.status.orgKey : null;
  const context = state.kind === 'ready' ? state.status.credentialContext : null;

  return (
    <div className="bg-card border-border rounded-lg border p-5" data-testid="polymarket-us-org-key-notice">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-base font-medium">Polymarket US</h2>
        <span
          className={`rounded px-2 py-0.5 text-[10px] font-bold ${
            orgKey ? 'bg-emerald-500/20 text-emerald-500 dark:text-emerald-400' : 'bg-secondary text-muted-foreground'
          }`}
        >
          {orgKey ? 'Org key in use' : 'No org key'}
        </span>
      </div>
      {state.kind === 'error' ? (
        <p className="text-xs text-red-500 dark:text-red-400">{state.message}</p>
      ) : orgKey ? (
        <p className="text-muted-foreground text-xs">
          Trading Polymarket US through the org key <span className="text-foreground font-medium">“{orgKey.label}”</span>{' '}
          <span className="font-mono">({tail(orgKey.keyId)})</span>. Your orders are tracked to you in the ledger; managers
          change the key under Org Settings.
        </p>
      ) : context === 'NOT_IN_ORG' ? (
        <p className="text-muted-foreground text-xs">You are not in an org, so there is no Polymarket US key to trade through.</p>
      ) : context === 'PERSONAL_KEY_MODE' ? (
        <p className="text-muted-foreground text-xs">
          Your account is in personal-key mode. Polymarket US trades only through the org key — ask a manager to switch you
          to org keys.
        </p>
      ) : (
        <p className="text-muted-foreground text-xs">
          No Polymarket US key on this org yet. Ask a manager to add the entity account’s Key ID and Secret under Org
          Settings.
        </p>
      )}
    </div>
  );
}

/** Back-compat alias for the Settings import. */
export { PolymarketUsOrgKeyNotice as PolymarketUsKeyCard };
